import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ActionIcon, Collapse, Checkbox } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconStackMiddle, IconLayersSubtract, IconMap } from '@tabler/icons-react';
import { MapEstablishmentList } from './Widgets/MapEstablishmentList';
import LeftPanel from './Widgets/LeftPanel';
import RightPanel from './Widgets/RightPanel';
import { useFilters } from '../../../Context/FiltersContext';

import OxygenSaturationSummary from './../../Establishment/Establishments/Summary/Widgets/OxygenSaturationSummary';
import DissolvedOxygenSummary from '../../Establishment/Establishments/Summary/Widgets/DissolvedOxygenSummary';
import TemperatureSummary from '../../Establishment/Establishments/Summary/Widgets/TemperatureSummary';
import SalinitySummary from '../../Establishment/Establishments/Summary/Widgets/SalinitySummary';
import { getEstablishment } from '../../../slices/thunks';
import { getInformationMonitoringFAN, getPolygonsSubpesca, getInformationSnifaCoords, getEstablishments, getProductiveCycles, getAreas, getNeighbourhoods, getRegions, getCompanies, getWeeklyReportFANData } from '../../../helpers/data_sped/backend_helper';
import { getEstablishmentListForMap } from "../../../helpers/establishment/backend_helper";

import { api } from '../../../config'
import { COLOR_BUTTONS_MAP_SPED_ORIGINAL } from '../../../Components/General/Map/MapMultiple/utils/mapConstants';

const MAPBOX_STYLES = [
		{
			key: 'light',
			label: 'Light',
			icon: '/icons/light.webp',
			style: 'mapbox://styles/mapbox/light-v11'
		},
		{
			key: 'satellite',
			label: 'Satelite',
			icon: '/icons/satellite.webp',
			style: 'mapbox://styles/mapbox/satellite-streets-v12'
		},
		{
			key: 'streets',
			label: 'Streets',
			icon: '/icons/streets.webp',
			style: 'mapbox://styles/mapbox/streets-v12'
		}
	];

const LAYER_OPTIONS = [
    // { value: 'fan', label: 'FAN' },
    { value: 'establishments', label: 'Establecimientos' },
    // { value: 'snifa', label: 'SNIFA' },
    // { value: 'polygons', label: 'Polígonos' }
];

const DashboardVariable = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentWidgetIndex, setCurrentWidgetIndex] = useState(0);
    const [showLeftPanel, setShowLeftPanel] = useState(false);
    const [leftPanelSection, setLeftPanelSection] = useState("widgets");
    const [activeTab, setActiveTab] = useState('graficos');
    const [selectedMapStyle, setSelectedMapStyle] = useState(MAPBOX_STYLES[0].style);
    const [polygons, setPolygons] = useState([]);
    const [showLayerFilter, setShowLayerFilter] = useState(false);
    const [selectedLayers, setSelectedLayers] = useState(LAYER_OPTIONS.map(l => l.value));

    const [fanData, setFanData] = useState([]);
    const [establishmentData, setEstablishmentData] = useState([]);
    const [snifaData, setSnifaData] = useState([]);

    // SPED Data External
    const [establishmentExternalData, setEstablishmentExternalData] = useState([]);
    const [companyExternalData, setCompanyExternalData] = useState([]);
    const [areaExternalData, setAreaExternalData] = useState([]);
    const [neighbourhoodExternalData, setNeighbourhoodExternalData] = useState([]);
    const [regionExternalData, setRegionExternalData] = useState([]);

    const dispatch = useDispatch();

    const {
        establishments,
        setEstablishments,
        establishmentExternal,
        setEstablishmentExternal,
        companyExternal,
        setCompanyExternal,
        areaExternal,
        setAreaExternal,
        neighbourhoodExternal,
        setNeighbourhoodExternal,
        regionExternal,
        setRegionExternal,
        filteredEstablishments,
        mapEstablishments,
        isLoadingEstablishments,
        setIsLoadingEstablishments
    } = useFilters();

    const handleSelectEstablishment = useCallback((establishment) => {
        if (establishment === null) {
            setSearchParams({});
            setShowLeftPanel(false);
        } else {
            setSearchParams({
                code_siep: establishment.code_siep,
            });
            setShowLeftPanel(true);
            setShowLayerFilter(false); 
            setLeftPanelSection("widgets"); 
        }
    }, [setSearchParams]);
    const toggleLeftPanel = useCallback(() => {
        setShowLayerFilter(false);
        setLeftPanelSection(prev => prev === "widgets" ? null : "widgets");
    }, []);

    const toggleLeftStyleMap = useCallback(() => {
        setShowLayerFilter(false);
        setLeftPanelSection(prev => prev === "styles" ? null : "styles");
    }, []);

    const toggleLayerFilter = useCallback(() => {
        setLeftPanelSection(null);
        setShowLayerFilter(prev => !prev);
    }, []);


    const codeSiep = useMemo(() => searchParams.get('code_siep'), [searchParams]);

    const selectedEstablishment = useMemo(() => {
        const establishmentList = (!['', null].includes(api.REACT_APP_SPED_ORIGINAL_API_URL) ? establishmentExternal : establishments);

        if (!codeSiep || !establishmentList?.length) return null;

        const found = establishmentList.find(establishment => {
            const codeSiepEstablishment = establishment?.siep_code || establishment?.code_siep || establishment?.codigoSERNAPESCA || '';
            const codeSiepEstablishmentString = codeSiepEstablishment.toString();

            if (codeSiepEstablishmentString == codeSiep)  {
                return true;
            }
            const coordsPattern = `${establishment.latitude}_${establishment.longitude}`;
            return coordsPattern === codeSiep;
        });

        if (found) {
            return {
                _id: String(found._id || found.id || found?.pk || 'unknown'),
                nombre_centro: String(found.titulares || found.name || found?.nombre || 'Sin nombre'),
                region_centro: String((typeof found.region === 'string' ? found.region : found.region?.name) || found?.nombreRegion|| 'Región no especificada'),
                siep_code: String(found.siep_code || found.code_siep || found?.codigoSERNAPESCA || `${found.latitude}_${found.longitude}`),
                state: String(found.state || 'green')
            };
        }

        return null;
    }, [codeSiep, establishments, establishmentExternal]);

    const widgets = useMemo(() => [
        {
            component: OxygenSaturationSummary,
            title: 'Saturación Oxígeno',
            um: '%',
        },
        {
            component: DissolvedOxygenSummary,
            title: 'Oxígeno Disuelto',
            um: 'mg/L',
        },
        {
            component: TemperatureSummary,
            title: 'Temperatura',
            um: '°C',
        },
        {
            component: SalinitySummary,
            title: 'Salinidad',
            um: 'psu',

        }
    ], []);

    const normalizeState = (state) => {
        switch(state){
            case 'Normal':
                return 'green'
            case 'Bajo':
                return 'orange'
            case 'Medio':
                return 'orangehover'
            case 'Alto':
                return 'red'
            default:
                return 'green'
        }
    }

    const getEstablishmentsFilteredByCycleData = (
        establishments,
        productiveCycles,
        stateEstablishments
    ) => {
        return (
            establishments
                .map(establishment => {
                    const cycleProductiveEstablishment = (
                        productiveCycles
                            .find(
                                productiveCycle => 
                                    productiveCycle.nombreEstablecimiento === 
                                    establishment.nombre
                            )
                    );

                    const currentEstablishmentState = stateEstablishments.find(stateEstablishment => {
                        if(establishment.nombreInterno === stateEstablishment.name) return stateEstablishment
                    })

                    if(
                        cycleProductiveEstablishment || 
                        (
                            establishment.nombreTipoUnidad == 'Acopio' &&
                            !["N/A", "Inactivo"].includes(establishment.etapaProductiva) // Exclude etapaProductiva (N/A, Inactivo)
                        )
                    ) return {state: normalizeState(currentEstablishmentState?.state) || 'Normal', ...establishment};
                })
                .filter( establishment => establishment != undefined)
        )
    }

    const getStateEstablishmentsFromWeeklyReportFANData = (weeklyReportFANData) => {
        let stateEstablishments = [];

        (weeklyReportFANData?.centrosRegiones || [])
            .map(
                dataRegion => {
                    (dataRegion?.dataCentrosPorArea || [])
                        .map(
                            dataArea => {
                                (dataArea?.estadosCentrosArea || [])
                                    .map(dataCentro => {
                                        stateEstablishments.push({
                                            name: dataCentro?.centro__nombreInterno,
                                            state: dataCentro?.estado__nombre
                                        })
                                    })
                            })
                })
        
        return stateEstablishments;
    }

    const handlePrevWidget = useCallback(() => {
        setCurrentWidgetIndex(prev =>
            prev === 0 ? widgets.length - 1 : prev - 1
        );
    }, [widgets.length]);

    const handleNextWidget = useCallback(() => {
        setCurrentWidgetIndex(prev =>
            prev === widgets.length - 1 ? 0 : prev + 1
        );
    }, [widgets.length]);

    const currentWidget = useMemo(() => widgets[currentWidgetIndex], [widgets, currentWidgetIndex]);

    useEffect(() => {
        document.title = 'SPED - Dashboard Variable';
    }, []);


    useEffect(() => {
        const loadEstablishments = async () => {
            try {
                setIsLoadingEstablishments(true);

                const [
                    fanResponse,
                    establishmentResponse,
                    snifaResponse,
                ] = await Promise.all([
                    getInformationMonitoringFAN(),
                    getEstablishmentListForMap(),
                    getInformationSnifaCoords(),
                ]);

                const fanWithHex = Array.isArray(fanResponse)
                    ? fanResponse.map(item => ({ ...item, showHexagons: false }))
                    : [];
                const snifaWithStateAndHex = Array.isArray(snifaResponse)
                    ? snifaResponse.map(item => ({ ...item, state: 'orangehover', showHexagons: false }))
                    : [];
                
                setFanData(fanWithHex);
                setEstablishmentData(Array.isArray(establishmentResponse?.data) ? establishmentResponse.data : []);
                setSnifaData(snifaWithStateAndHex);

                // if REACT_APP_SPED_ORIGINAL_API_URL defined get data external to dashboard map 
                if(!['', null].includes(api.REACT_APP_SPED_ORIGINAL_API_URL)){
                    const [
                        establishmentExternalResponse,
                        productiveCycleExternalResponse,
                        weeklyReportFANDataResponse,
                        // companyExternalResponse,
                        // areaExternalResponse,
                        // neighbourhoodsExternalResponse,
                        // regionExternalResponse
                    ] = await Promise.all([
                        getEstablishments(),
                        getProductiveCycles(),
                        getWeeklyReportFANData()
                        // getCompanies(),
                        // getAreas(),
                        // getNeighbourhoods(),
                        // getRegions()
                    ]);
                    
                    // Get data from responses
                    const productiveCycles = productiveCycleExternalResponse?.data || [];
                    const establishments = establishmentExternalResponse?.data || [];
                    const weeklyReportFANData = weeklyReportFANDataResponse || {};

                    // Get state establishment by report FAN Data
                    const stateEstablishments = getStateEstablishmentsFromWeeklyReportFANData(weeklyReportFANData);

                    // filter establishments by active productive cycles
                    const establishmentsFiltered = getEstablishmentsFilteredByCycleData(establishments, productiveCycles, stateEstablishments);
                    
                    setEstablishmentExternalData(establishmentsFiltered);
                    setEstablishmentExternal(
                        establishmentsFiltered
                    )

                    // setCompanyExternalData(Array.isArray(companyExternalResponse?.data) ? companyExternalResponse.data : []);
                    // setCompanyExternal(companyExternalResponse?.data || []);

                    // setAreaExternalData(Array.isArray(areaExternalResponse?.data) ? areaExternalResponse.data : []);
                    // setAreaExternal(areaExternalResponse?.Area || []);

                    // setNeighbourhoodExternalData(Array.isArray(neighbourhoodsExternalResponse?.data) ? neighbourhoodsExternalResponse.data : []);
                    // setNeighbourhoodExternal(neighbourhoodsExternalResponse?.Barrio || []);

                    // setRegionExternalData(Array.isArray(regionExternalResponse?.data) ? regionExternalResponse.data : []);
                    // setRegionExternal(regionExternalResponse?.Region || []);
                }

                // Por defecto, mostrar todo
                setEstablishments([
                    ...fanWithHex,
                    ...(Array.isArray(establishmentResponse?.data) ? establishmentResponse.data : []),
                    ...snifaWithStateAndHex
                ]);

            } catch (error) {
                console.error('Error loading establishments:', error);
            } finally {
                setIsLoadingEstablishments(false);
            }
        };

        loadEstablishments();
    }, [setEstablishments, setIsLoadingEstablishments]);

    useEffect(() => {
        let result = [];
        if (selectedLayers.includes('fan')) result = result.concat(fanData);
        if (selectedLayers.includes('establishments')) result = result.concat(establishmentData);
        if (selectedLayers.includes('snifa')) result = result.concat(snifaData);
        setEstablishments(result);
    }, [selectedLayers, fanData, establishmentData, snifaData, setEstablishments]);


    useEffect(() => {
        const loadPolygons = async () => {
            try {
                const polygonsData = await getPolygonsSubpesca();
                setPolygons(polygonsData);
            } catch (error) {
                console.error('Error loading polygons:', error);
            }
        };
        loadPolygons();
    }, []);

    
    const polygonsByLayer = useMemo(() => {
        return selectedLayers.includes('polygons') ? polygons : [];
    }, [polygons, selectedLayers]);

    useEffect(() => {
        const code_siep = searchParams.get('code_siep');
        if (code_siep && code_siep !== 'all') {
            dispatch(getEstablishment(code_siep));
        }
    }, [searchParams, dispatch]);


    useEffect(() => {
        const code_siep = searchParams.get('code_siep');
        setShowLeftPanel(code_siep && code_siep !== 'all');
    }, [searchParams]);


    const actionButtonsComponent = (
        <section className='d-flex flex-column gap-3 button-action-show-left-panel'>
            <ActionIcon
                onClick={toggleLeftPanel}
                size="lg"
                radius="xl"
                variant="filled"
                sx={{
                    backgroundColor: COLOR_BUTTONS_MAP_SPED_ORIGINAL.primary,
                    color: 'white', // icon color
                }}
                style={{
                    visibility: showLeftPanel ? 'visible' : 'hidden'
                }}
            >
                {leftPanelSection === "widgets" ?  <IconChevronLeft size={18}/> : <IconChevronRight size={18} /> }
            </ActionIcon>
            <ActionIcon
                onClick={toggleLeftStyleMap}
                size="lg"
                radius="xl"
                variant="filled"
                sx={{
                    backgroundColor: COLOR_BUTTONS_MAP_SPED_ORIGINAL.primary,
                    color: 'white', // icon color
                }}
            >
                <IconMap size={18} />
            </ActionIcon>
            <ActionIcon
                onClick={toggleLayerFilter}
                size="lg"
                radius="xl"
                variant="filled"
                sx={{
                    backgroundColor: COLOR_BUTTONS_MAP_SPED_ORIGINAL.primary,
                    color: 'white', // icon color
                }}
                title="Capas"
            >
                <IconStackMiddle size={18} />
            </ActionIcon>
        </section>
    );

    const leftPanelComponent = (
        <LeftPanel
            showLeftPanel={showLeftPanel}
            leftPanelSection={leftPanelSection}
            selectedEstablishment={selectedEstablishment}
            isLoadingEstablishments={isLoadingEstablishments}
            searchParams={searchParams}
            codeSiep={codeSiep}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            LAYER_OPTIONS={LAYER_OPTIONS}
            MAPBOX_STYLES={MAPBOX_STYLES}
            selectedMapStyle={selectedMapStyle}
            setSelectedMapStyle={setSelectedMapStyle}
            showLayerFilter={showLayerFilter}
            setShowLayerFilter={setShowLayerFilter}
            selectedLayers={selectedLayers}
            setSelectedLayers={setSelectedLayers}
        />
    );

    const rightPanelComponent = (
        <RightPanel
            widgets={widgets}
            selectedEstablishment={selectedEstablishment}
            currentWidgetIndex={currentWidgetIndex}
            setCurrentWidgetIndex={setCurrentWidgetIndex}
            codeSiep={codeSiep}
        />
    );

    return (
		<React.Fragment>
            <MapEstablishmentList
                selectedEstablishmentSiep={codeSiep}
                onSelectEstablishment={handleSelectEstablishment}
                zoom={5}
                fullscreen={true}
                establishments={mapEstablishments}
                isLoadingEstablishments={isLoadingEstablishments}
                mapboxStyles={selectedMapStyle}
                polygons={polygonsByLayer}
                leftPanel={leftPanelComponent}
                rightPanel={rightPanelComponent}
                actionButtons={actionButtonsComponent}
            />
		</React.Fragment>
	);
};

export default DashboardVariable;
